'use strict';

// ========================================
// 3D NETWORK PARTICLE ANIMATION
// Award-Winning Hero Background
// ========================================

// Utility: debounce function (scoped to avoid naming collisions)
const networkDebounce = (fn, delay) => {
    let timer = null;
    return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => fn(...args), delay);
    };
};

class NetworkAnimation {
    constructor(canvasId) {
        this.canvas = document.getElementById(canvasId);
        if (!this.canvas) return;

        this.ctx = this.canvas.getContext('2d');
        this.particles = [];
        this.mouse = { x: null, y: null, radius: 150 };
        this.animationId = null;
        this.isActive = true;

        const isMobile = window.innerWidth < 768;

        // Configuration
        this.config = {
            particleCount: isMobile ? 40 : 80,
            particleSize: { min: 1, max: 3 },
            speed: isMobile ? 0.35 : 0.5,
            connectionDistance: isMobile ? 120 : 150,
            mouseRepelDistance: 100,
            fpsLimit: isMobile ? 30 : 60,
            colors: {
                particle: 'rgba(45, 104, 196, 0.8)',
                particleGlow: 'rgba(136, 71, 199, 0.6)',
                line: 'rgba(45, 104, 196, 0.15)',
                lineActive: 'rgba(0, 212, 255, 0.4)'
            }
        };

        this.init();
    }

    init() {
        this.resize();
        this.createParticles();
        this.bindEvents();
        this.animate();
    }

    resize() {
        const container = this.canvas.parentElement;
        this.canvas.width = container.offsetWidth;
        this.canvas.height = container.offsetHeight;
    }

    createParticles() {
        this.particles = [];
        for (let i = 0; i < this.config.particleCount; i++) {
            this.particles.push(new Particle(this));
        }
    }

    bindEvents() {
        const debouncedResize = networkDebounce(() => {
            this.resize();
            this.createParticles();
        }, 150);
        window.addEventListener('resize', debouncedResize);

        const container = this.canvas.parentElement;
        container.addEventListener('mousemove', (e) => {
            const rect = this.canvas.getBoundingClientRect();
            this.mouse.x = e.clientX - rect.left;
            this.mouse.y = e.clientY - rect.top;
        });

        container.addEventListener('mouseleave', () => {
            this.mouse.x = null;
            this.mouse.y = null;
        });

        document.addEventListener('visibilitychange', () => this.checkActiveState());

        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                this.isInViewport = entry.isIntersecting;
                this.checkActiveState();
            });
        }, { threshold: 0.1 });
        observer.observe(container);

        const sectionEl = this.canvas.closest('.section');
        if (sectionEl) {
            const classObserver = new MutationObserver(() => this.checkActiveState());
            classObserver.observe(sectionEl, { attributes: true, attributeFilter: ['class'] });
        }
    }

    checkActiveState() {
        const sectionEl = this.canvas.closest('.section');
        const isSectionActive = sectionEl ? sectionEl.classList.contains('section--is-active') : true;
        const shouldBeActive = this.isInViewport && !document.hidden && isSectionActive;

        if (shouldBeActive && !this.isActive) {
            this.isActive = true;
            this.animate();
        } else if (!shouldBeActive) {
            this.isActive = false;
        }
    }

    animate(currentTime) {
        if (!this.isActive) return;

        // FPS Throttling
        this.lastTime = this.lastTime || 0;
        const delta = currentTime - this.lastTime;
        const interval = 1000 / this.config.fpsLimit;

        if (delta < interval) {
            this.animationId = requestAnimationFrame((t) => this.animate(t));
            return;
        }
        this.lastTime = currentTime - (delta % interval);

        const ctx = this.ctx;
        ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

        // Update and draw particles
        this.particles.forEach(p => {
            p.update();
            p.draw(ctx);
        });

        // Draw connections
        this.drawConnections(ctx);

        this.animationId = requestAnimationFrame((t) => this.animate(t));
    }

    drawConnections(ctx) {
        const particles = this.particles;
        const count = particles.length;
        const maxDistSq = this.config.connectionDistance * this.config.connectionDistance;
        const mouseRadiusSq = this.mouse.radius * this.mouse.radius;
        const msX = this.mouse.x;
        const msY = this.mouse.y;

        for (let i = 0; i < count; i++) {
            const p1 = particles[i];
            for (let j = i + 1; j < count; j++) {
                const p2 = particles[j];
                const dx = p1.x - p2.x;
                const dy = p1.y - p2.y;
                const distSq = dx * dx + dy * dy;

                if (distSq < maxDistSq) {
                    const dist = Math.sqrt(distSq);
                    const opacity = 1 - (dist / this.config.connectionDistance);

                    // Check if near mouse for highlight
                    let isNearMouse = false;
                    if (msX && msY) {
                        const midX = (p1.x + p2.x) * 0.5;
                        const midY = (p1.y + p2.y) * 0.5;
                        const mdx = midX - msX;
                        const mdy = midY - msY;
                        isNearMouse = (mdx * mdx + mdy * mdy) < mouseRadiusSq;
                    }

                    ctx.beginPath();
                    ctx.moveTo(p1.x, p1.y);
                    ctx.lineTo(p2.x, p2.y);

                    if (isNearMouse) {
                        ctx.strokeStyle = `rgba(0, 212, 255, ${opacity * 0.6})`;
                        ctx.lineWidth = 1.5;
                    } else {
                        ctx.strokeStyle = `rgba(45, 104, 196, ${opacity * 0.2})`;
                        ctx.lineWidth = 1;
                    }
                    ctx.stroke();
                }
            }
        }
    }
}

class Particle {
    constructor(network) {
        this.network = network;
        this.canvas = network.canvas;
        this.ctx = network.ctx;
        this.config = network.config;

        this.x = Math.random() * this.canvas.width;
        this.y = Math.random() * this.canvas.height;
        this.size = Math.random() * (this.config.particleSize.max - this.config.particleSize.min) + this.config.particleSize.min;

        this.speedX = (Math.random() - 0.5) * this.config.speed;
        this.speedY = (Math.random() - 0.5) * this.config.speed;

        this.baseX = this.x;
        this.baseY = this.y;

        // Pulse animation
        this.pulsePhase = Math.random() * Math.PI * 2;
        this.pulseSpeed = 0.02 + Math.random() * 0.02;
    }

    update() {
        // Pulse effect
        this.pulsePhase += this.pulseSpeed;
        const pulseFactor = 1 + Math.sin(this.pulsePhase) * 0.3;

        // Mouse interaction - optimized with squared distance
        const msX = this.network.mouse.x;
        const msY = this.network.mouse.y;
        if (msX && msY) {
            const dx = msX - this.x;
            const dy = msY - this.y;
            const distSq = dx * dx + dy * dy;
            const repelDistSq = this.config.mouseRepelDistance * this.config.mouseRepelDistance;

            if (distSq < repelDistSq) {
                const distance = Math.sqrt(distSq);
                const force = (this.config.mouseRepelDistance - distance) / this.config.mouseRepelDistance;
                const angle = Math.atan2(dy, dx);
                this.x -= Math.cos(angle) * force * 3;
                this.y -= Math.sin(angle) * force * 3;
            }
        }

        // Movement
        this.x += this.speedX;
        this.y += this.speedY;

        // Boundary check with wrap-around
        if (this.x < 0) this.x = this.canvas.width;
        if (this.x > this.canvas.width) this.x = 0;
        if (this.y < 0) this.y = this.canvas.height;
        if (this.y > this.canvas.height) this.y = 0;

        this.currentSize = this.size * pulseFactor;
    }

    draw(ctx) {
        const x = this.x;
        const y = this.y;
        const radius = this.currentSize;

        // Fast glow effect (no gradient)
        ctx.beginPath();
        ctx.arc(x, y, radius * 3, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(45, 104, 196, 0.15)';
        ctx.fill();

        // Core particle
        ctx.beginPath();
        ctx.arc(x, y, radius, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(222, 232, 247, 0.9)';
        ctx.fill();
    }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', function () {
    // Small delay to ensure canvas is properly sized
    setTimeout(() => {
        new NetworkAnimation('network-canvas');
    }, 100);
});

// Also initialize on section change (for single-page navigation)
$(document).ready(function () {
    // Re-initialize when returning to home section
    $(document).on('click', '.side-nav li:first-child, .outer-nav li:first-child', function () {
        setTimeout(() => {
            const canvas = document.getElementById('network-canvas');
            if (canvas && !canvas.hasAttribute('data-initialized')) {
                new NetworkAnimation('network-canvas');
                canvas.setAttribute('data-initialized', 'true');
            }
        }, 500);
    });
});

// ========================================
// HUD CLOCK - Mission Control Time Display
// ========================================

function updateHUDClock() {
    const timeElement = document.getElementById('hud-time');
    if (timeElement) {
        const now = new Date();
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        const seconds = String(now.getSeconds()).padStart(2, '0');
        timeElement.textContent = `${hours}:${minutes}:${seconds}`;
    }
}

// Update clock every second
setInterval(updateHUDClock, 1000);

// Initialize on load
document.addEventListener('DOMContentLoaded', function () {
    updateHUDClock();
});
